import type {AddressSearchResult} from '../api/types';

/**
 * The line under a search result: its numbers, and «ingen gateadresse» for a property found by its
 * gnr/bnr that has no street address (SB-440). Such a result's title is its number and kommune
 * («Gnr 101 / Bnr 23, Orkland»), which LCF also parses back when the page searches it again.
 */
export function addressCaption(
    result: Pick<AddressSearchResult, 'gnr' | 'bnr' | 'fnr' | 'has_address'>,
): string {
    const numbers = `gnr: ${result.gnr}, bnr: ${result.bnr}` + (result.fnr ? `, fnr: ${result.fnr}` : '');
    return result.has_address === false ? `${numbers} · ingen gateadresse` : numbers;
}

/**
 * The search box shows the server's results as they come (SB-440). An Autocomplete filters its
 * options by their label by default, and that hid every result whose label does not contain the
 * typed text: «101/23 Orkland» is answered by «Gnr 101 / Bnr 23, Orkland», «Lovisen 9» by
 * «Lovisenlund 9, 0287 OSLO» (the fuzzy tier), and no street address contains «101/23».
 */
export function keepServerResults<T>(options: T[]): T[] {
    return options;
}

// gnr/bnr or gnr/bnr/fnr; not «5059/101/23», whose four-digit first number is the kommune.
const GNR_BNR_WITHOUT_KOMMUNE = /^\s*(?:gnr\.?\s*)?(?!\d{4}\s*\/\s*\d+\s*\/)\d+\s*\/\s*\d+(?:\s*\/\s*\d+)?\s*$/i;

/**
 * The hint under the search box when the query is gnr/bnr with no kommune (SB-440): the numbers
 * repeat in every kommune, and a property with no street address is found only with its kommune.
 */
export function gnrBnrHint(query: string): string | undefined {
    return GNR_BNR_WITHOUT_KOMMUNE.test(query)
        ? 'Gnr/bnr finnes i alle kommuner: legg til kommunen («101/23 Orkland») for å finne eiendommen, også en uten gateadresse.'
        : undefined;
}
